// Browser-side raster → SVG vectorizer (v2 — high-quality).
//
// Pipeline:
//   1. Image → working canvas (192×192 default, up to 512)
//   2. Luminance buffer (Rec. 709)
//   3. Optional bilinear / box-blur pre-pass (anti-alias jagged edges before threshold)
//   4. Threshold mask
//   5. **Marching Squares with sub-pixel linear interpolation** — smooth boundaries
//   6. Stitch edge segments into closed polygons by endpoint matching
//   7. Detect holes (donut shapes) via point-in-polygon containment + even-odd fill
//   8. Douglas-Peucker simplification (configurable tolerance)
//   9. Optional Chaikin smoothing pass for curvier paths
//  10. Optional quadratic Bézier emission (Q commands) for vector-grade smoothness
//  11. Emit SVG with `<path d="...">` per shape, fill-rule="evenodd"
//
// Quality presets bundle these knobs into Low/Medium/High/Ultra.

export type VectorizeMode = 'monochrome' | 'posterize' | 'pixel';
export type Quality = 'low' | 'medium' | 'high' | 'ultra';

export interface VectorizeOptions {
  mode: VectorizeMode;
  workingSize: number;        // 64..512 — internal raster resolution
  threshold: number;          // 0..1 (monochrome only)
  posterizeLevels: number;    // 2..6
  blur: number;               // 0..3 — pre-filter passes (each is a 3×3 box blur)
  simplify: number;           // 0..2 — Douglas-Peucker tolerance (working-pixel units)
  smoothing: number;          // 0..3 — Chaikin smoothing passes
  bezier: boolean;            // emit quadratic Bézier (Q) instead of L commands
  invert: boolean;
}

export const QUALITY_PRESETS: Record<Quality, VectorizeOptions> = {
  low:    { mode: 'monochrome', workingSize: 96,  threshold: 0.5, posterizeLevels: 3, blur: 0, simplify: 0.8, smoothing: 0, bezier: false, invert: false },
  medium: { mode: 'monochrome', workingSize: 192, threshold: 0.5, posterizeLevels: 3, blur: 1, simplify: 0.4, smoothing: 1, bezier: true,  invert: false },
  high:   { mode: 'monochrome', workingSize: 320, threshold: 0.5, posterizeLevels: 4, blur: 2, simplify: 0.3, smoothing: 2, bezier: true,  invert: false },
  ultra:  { mode: 'monochrome', workingSize: 512, threshold: 0.5, posterizeLevels: 5, blur: 2, simplify: 0.2, smoothing: 2, bezier: true,  invert: false }
};

export const DEFAULT_VECTORIZE: VectorizeOptions = QUALITY_PRESETS.high;

const TILE = 64; // SVG viewBox

export async function vectorizeFile(file: File, opts: Partial<VectorizeOptions> = {}): Promise<string> {
  const o = { ...DEFAULT_VECTORIZE, ...opts };
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    return vectorizeImage(img, o);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function vectorizeImage(img: HTMLImageElement, opts: Partial<VectorizeOptions> = {}): string {
  const o = { ...DEFAULT_VECTORIZE, ...opts };
  const W = clamp(o.workingSize | 0, 32, 512);
  const H = W;

  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas 2d context unavailable');
  // High-quality downsample
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, W, H);
  const data = ctx.getImageData(0, 0, W, H).data;

  // Luminance buffer
  let lum = new Float32Array(W * H);
  for (let i = 0; i < W * H; i++) {
    const r = data[i * 4], g = data[i * 4 + 1], b = data[i * 4 + 2];
    let l = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    if (o.invert) l = 1 - l;
    lum[i] = l;
  }
  // Pre-smoothing — anti-aliases jagged edges before threshold
  for (let p = 0; p < o.blur; p++) lum = boxBlur3x3(lum, W, H);

  if (o.mode === 'pixel') return pixelMosaic(lum, W, H, o.posterizeLevels);

  const levels: { thr: number; fill: string }[] = o.mode === 'monochrome'
    ? [{ thr: o.threshold, fill: 'white' }]
    : posterizeLevels(o.posterizeLevels);

  // For each level, extract closed polygons (with hole detection) and emit one <path>.
  const allPaths: string[] = [];
  for (const lvl of levels) {
    const polys = marchingSquares(lum, W, H, lvl.thr);
    if (polys.length === 0) continue;

    // Process polygons: simplify → smooth → emit
    const processed = polys.map((poly) => {
      let p = poly;
      if (o.simplify > 0) p = simplify(p, o.simplify);
      for (let i = 0; i < o.smoothing; i++) p = chaikin(p);
      return p;
    }).filter((p) => p.length >= 3);

    if (processed.length === 0) continue;

    // Group by hole containment — outer + inner subpaths share a single <path>
    const grouped = groupHoles(processed);
    for (const group of grouped) {
      const d = group.map((poly, i) => polyToPathD(poly, W, H, o.bezier)).join(' ');
      allPaths.push(`<path d="${d}" fill="${lvl.fill}" fill-rule="evenodd"/>`);
    }
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${TILE} ${TILE}" width="${TILE}" height="${TILE}">
  <rect width="${TILE}" height="${TILE}" fill="#808080"/>
  ${allPaths.join('\n  ')}
</svg>`;
}

// ---------------------------------------------------------------------------
// Marching squares with linear interpolation (sub-pixel boundary extraction)
// ---------------------------------------------------------------------------

type Vec2 = [number, number];
type Seg = [Vec2, Vec2];

function marchingSquares(lum: Float32Array, W: number, H: number, thr: number): Vec2[][] {
  const segs: Seg[] = [];
  // Walk every 2×2 cell. (W-1) × (H-1) cells.
  for (let y = 0; y < H - 1; y++) {
    for (let x = 0; x < W - 1; x++) {
      const v00 = lum[y * W + x];
      const v10 = lum[y * W + x + 1];
      const v11 = lum[(y + 1) * W + x + 1];
      const v01 = lum[(y + 1) * W + x];
      const idx = (v00 >= thr ? 1 : 0) | (v10 >= thr ? 2 : 0) | (v11 >= thr ? 4 : 0) | (v01 >= thr ? 8 : 0);
      if (idx === 0 || idx === 15) continue;
      // Edge crossings — linear interp gives sub-pixel accuracy
      const top   = (): Vec2 => [x + (thr - v00) / (v10 - v00), y];
      const right = (): Vec2 => [x + 1, y + (thr - v10) / (v11 - v10)];
      const bot   = (): Vec2 => [x + (thr - v01) / (v11 - v01), y + 1];
      const left  = (): Vec2 => [x, y + (thr - v00) / (v01 - v00)];
      // Standard 16-case lookup. Saddle cases (5,10) split into two segments.
      // Segments are oriented so that the inside (>= thr) is on the LEFT of
      // travel direction — this makes outer contours wind CCW, holes CW.
      switch (idx) {
        case 1:  segs.push([top(), left()]); break;
        case 2:  segs.push([right(), top()]); break;
        case 3:  segs.push([right(), left()]); break;
        case 4:  segs.push([bot(), right()]); break;
        case 5:  segs.push([top(), left()]); segs.push([bot(), right()]); break;
        case 6:  segs.push([bot(), top()]); break;
        case 7:  segs.push([bot(), left()]); break;
        case 8:  segs.push([left(), bot()]); break;
        case 9:  segs.push([top(), bot()]); break;
        case 10: segs.push([right(), bot()]); segs.push([left(), top()]); break;
        case 11: segs.push([right(), bot()]); break;
        case 12: segs.push([left(), right()]); break;
        case 13: segs.push([top(), right()]); break;
        case 14: segs.push([left(), top()]); break;
      }
    }
  }
  return stitchSegments(segs);
}

/** Stitch unordered edge segments into closed polygons by endpoint hashing. */
function stitchSegments(segs: Seg[]): Vec2[][] {
  if (segs.length === 0) return [];
  const QUANT = 1e5; // 0.01 pixel precision
  const key = (p: Vec2) => `${Math.round(p[0] * QUANT)}|${Math.round(p[1] * QUANT)}`;

  // For each endpoint, list of segments starting there (we only walk forward).
  const startMap = new Map<string, number[]>();
  for (let i = 0; i < segs.length; i++) {
    const k = key(segs[i][0]);
    let arr = startMap.get(k);
    if (!arr) { arr = []; startMap.set(k, arr); }
    arr.push(i);
  }

  const used = new Uint8Array(segs.length);
  const polys: Vec2[][] = [];
  for (let i = 0; i < segs.length; i++) {
    if (used[i]) continue;
    used[i] = 1;
    const poly: Vec2[] = [segs[i][0], segs[i][1]];
    let cur = segs[i][1];
    let safety = segs.length + 4;
    while (safety-- > 0) {
      const candidates = startMap.get(key(cur));
      if (!candidates) break;
      let nextIdx = -1;
      for (const c of candidates) if (!used[c]) { nextIdx = c; break; }
      if (nextIdx === -1) break;
      used[nextIdx] = 1;
      const nextSeg = segs[nextIdx];
      const startKey = key(poly[0]);
      const endKey = key(nextSeg[1]);
      if (endKey === startKey) {
        polys.push(poly); // closed
        break;
      }
      poly.push(nextSeg[1]);
      cur = nextSeg[1];
    }
  }
  return polys;
}

// ---------------------------------------------------------------------------
// Hole grouping (even-odd fill)
// ---------------------------------------------------------------------------

/**
 * Group polygons so each "outer" shape carries its inner holes as subpaths.
 * Using even-odd fill in SVG, an outer + inner subpath = donut.
 * Containment test is point-in-polygon on the first vertex.
 */
function groupHoles(polys: Vec2[][]): Vec2[][][] {
  const sorted = polys.slice().sort((a, b) => Math.abs(area(b)) - Math.abs(area(a)));
  const groups: Vec2[][][] = [];
  for (const p of sorted) {
    let parentIdx = -1;
    for (let g = 0; g < groups.length; g++) {
      if (pointInPoly(p[0], groups[g][0])) { parentIdx = g; break; }
    }
    if (parentIdx === -1) groups.push([p]);
    else groups[parentIdx].push(p);
  }
  return groups;
}

function area(poly: Vec2[]): number {
  let a = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    a += (poly[j][0] + poly[i][0]) * (poly[j][1] - poly[i][1]);
  }
  return a * 0.5;
}

function pointInPoly(p: Vec2, poly: Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0], yi = poly[i][1];
    const xj = poly[j][0], yj = poly[j][1];
    if ((yi > p[1]) !== (yj > p[1]) && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi + 1e-12) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

// ---------------------------------------------------------------------------
// Smoothing (Chaikin) and simplification (Douglas-Peucker)
// ---------------------------------------------------------------------------

/** One Chaikin pass — replaces each edge AB with two new points 1/4 and 3/4 along it.
 * After 1-2 passes, polylines look like smooth curves. Closed polygon variant. */
function chaikin(poly: Vec2[]): Vec2[] {
  if (poly.length < 3) return poly;
  const out: Vec2[] = [];
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    out.push([0.75 * a[0] + 0.25 * b[0], 0.75 * a[1] + 0.25 * b[1]]);
    out.push([0.25 * a[0] + 0.75 * b[0], 0.25 * a[1] + 0.75 * b[1]]);
  }
  return out;
}

function simplify(points: Vec2[], tolerance: number): Vec2[] {
  if (points.length < 3) return points.slice();
  const tol2 = tolerance * tolerance;
  return dp(points, 0, points.length - 1, tol2);
}

function dp(pts: Vec2[], first: number, last: number, tol2: number): Vec2[] {
  let maxD = 0; let idx = -1;
  const a = pts[first], b = pts[last];
  for (let i = first + 1; i < last; i++) {
    const d = pointSegDist2(pts[i], a, b);
    if (d > maxD) { maxD = d; idx = i; }
  }
  if (maxD > tol2 && idx !== -1) {
    return dp(pts, first, idx, tol2).slice(0, -1).concat(dp(pts, idx, last, tol2));
  }
  return [a, b];
}

function pointSegDist2(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-9) {
    const px = p[0] - a[0], py = p[1] - a[1];
    return px * px + py * py;
  }
  let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = a[0] + dx * t, cy = a[1] + dy * t;
  const ex = p[0] - cx, ey = p[1] - cy;
  return ex * ex + ey * ey;
}

// ---------------------------------------------------------------------------
// Pre-filtering
// ---------------------------------------------------------------------------

/** 3×3 box blur — separable, in-place safe via swap buffers. */
function boxBlur3x3(src: Float32Array, W: number, H: number): Float32Array {
  const tmp = new Float32Array(src.length);
  // Horizontal pass
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const x0 = Math.max(0, x - 1), x2 = Math.min(W - 1, x + 1);
      tmp[y * W + x] = (src[y * W + x0] + src[y * W + x] + src[y * W + x2]) / 3;
    }
  }
  const out = new Float32Array(src.length);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const y0 = Math.max(0, y - 1), y2 = Math.min(H - 1, y + 1);
      out[y * W + x] = (tmp[y0 * W + x] + tmp[y * W + x] + tmp[y2 * W + x]) / 3;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Path emission
// ---------------------------------------------------------------------------

/** Convert polygon to SVG d attribute. If `bezier`, emit quadratic curves
 * via midpoint control: each segment AB becomes a Q from M(prev,A) to M(A,B)
 * with control A. This produces a natural-looking curve through the polygon. */
function polyToPathD(poly: Vec2[], W: number, H: number, bezier: boolean): string {
  const sx = TILE / W, sy = TILE / H;
  const fmt = (n: number) => n.toFixed(2);
  if (poly.length < 3) return '';
  if (!bezier) {
    let d = `M ${fmt(poly[0][0] * sx)} ${fmt(poly[0][1] * sy)}`;
    for (let i = 1; i < poly.length; i++) d += ` L ${fmt(poly[i][0] * sx)} ${fmt(poly[i][1] * sy)}`;
    return d + ' Z';
  }
  // Quadratic Bézier with midpoint chaining (closed loop).
  const n = poly.length;
  const mid = (i: number, j: number): Vec2 => [
    (poly[i][0] + poly[j][0]) * 0.5 * sx,
    (poly[i][1] + poly[j][1]) * 0.5 * sy
  ];
  const m0 = mid(n - 1, 0);
  let d = `M ${fmt(m0[0])} ${fmt(m0[1])}`;
  for (let i = 0; i < n; i++) {
    const cx = poly[i][0] * sx, cy = poly[i][1] * sy;
    const m = mid(i, (i + 1) % n);
    d += ` Q ${fmt(cx)} ${fmt(cy)} ${fmt(m[0])} ${fmt(m[1])}`;
  }
  return d + ' Z';
}

// ---------------------------------------------------------------------------
// Pixel mosaic and posterize-level helpers
// ---------------------------------------------------------------------------

function pixelMosaic(lum: Float32Array, W: number, H: number, levels: number): string {
  const cell = TILE / W;
  const rects: string[] = [];
  const N = Math.max(2, levels);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const v = lum[y * W + x];
      const q = Math.round(v * (N - 1)) / (N - 1);
      if (Math.abs(q - 0.5) < 0.1) continue;
      const fill = q > 0.5 ? 'white' : 'black';
      const opacity = (Math.abs(q - 0.5) * 2).toFixed(2);
      rects.push(
        `<rect x="${(x * cell).toFixed(2)}" y="${(y * cell).toFixed(2)}" ` +
        `width="${cell.toFixed(2)}" height="${cell.toFixed(2)}" ` +
        `fill="${fill}" fill-opacity="${opacity}"/>`
      );
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${TILE} ${TILE}" width="${TILE}" height="${TILE}">
  <rect width="${TILE}" height="${TILE}" fill="#808080"/>
  ${rects.join('\n  ')}
</svg>`;
}

function posterizeLevels(n: number): { thr: number; fill: string }[] {
  const out: { thr: number; fill: string }[] = [];
  for (let i = 1; i <= n; i++) {
    const t = i / (n + 1);
    const grey = Math.round(t * 255);
    out.push({ thr: t, fill: `rgb(${grey},${grey},${grey})` });
  }
  return out;
}

function clamp(v: number, lo: number, hi: number) { return Math.max(lo, Math.min(hi, v)); }

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image decode failed'));
    img.src = src;
  });
}
